/**
 * The chat in the portal's side panel, live. Head office reads every chat here;
 * it replies only on its own inboxes (Accounts, Customer service), which a take
 * over moves a chat into. Peace of Sleep chats turn the panel gold (pos-themed).
 */

import { useEffect, useRef, useState } from 'react';
import type { LiveChat, LiveMessage } from './liveData';
import { IN_PLAY, mediaUrl, splitButtons, useChatMessages } from './liveData';
import { StageBadge } from './chatBits';
import { whenLabel } from './LiveView';

interface Props {
  chat: LiveChat;
  onClose: () => void;
  onReassign: (chat: LiveChat) => void;
  onTakeOver: (chat: LiveChat) => void;
  onCloseChat: (chat: LiveChat) => void;
  onOpenLead: (chat: LiveChat) => void;
  /** Resolves true when the message went. */
  onSend: (chat: LiveChat, text: string, signWithName: boolean) => Promise<boolean>;
  /** Dr C on this chat (Phase 2A): hand it back to Dr C, or switch Dr C off for this chat. */
  onDrc?: (chat: LiveChat, action: 'return_to_ai' | 'ai_off') => void;
}

const TICKS: Record<string, string> = { pending: '🕓', sent: '✓', delivered: '✓✓', read: '✓✓', failed: '⚠️' };

/** A customer's photo or voice note, through a short-lived private link. */
function Media({ message }: { message: LiveMessage }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    if (message.mediaPath) void mediaUrl(message.mediaPath).then((u) => { if (live) setUrl(u); });
    return () => { live = false; };
  }, [message.mediaPath]);
  if (!message.mediaPath) return null;
  if (!url) return <span className="text-xs text-muted">Loading file…</span>;
  const mime = message.mediaMime ?? '';
  if (mime.startsWith('image/')) return <a href={url} target="_blank" rel="noreferrer"><img className="wa-bubble-media" src={url} alt="Photo from the customer" /></a>;
  if (mime.startsWith('audio/')) return <audio className="wa-bubble-media" controls src={url} />;
  if (mime.startsWith('video/')) return <video className="wa-bubble-media" controls src={url} />;
  return <a href={url} target="_blank" rel="noreferrer">Open file</a>;
}

export default function ChatPanel({ chat, onClose, onReassign, onTakeOver, onCloseChat, onOpenLead, onSend, onDrc }: Props) {
  const messages = useChatMessages(chat.id);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  // Sign as the team by default; tick to add your own first name (Gary, 14 Oct).
  const [signWithName, setSignWithName] = useState(false);
  const thread = useRef<HTMLDivElement>(null);
  const inPlay = IN_PLAY.includes(chat.stage);
  const headOffice = chat.inbox !== 'leads';
  const canTakeOver = inPlay && !headOffice && chat.stage !== 'waiting_customer';

  useEffect(() => {
    const el = thread.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, chat.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function send() {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    const ok = await onSend(chat, text, signWithName);
    setSending(false);
    if (ok) setDraft('');
  }

  return (
    <>
      <div className="side-panel-overlay" onClick={onClose} />
      <aside
        className={`side-panel${chat.brand === 'pos' ? ' pos-themed' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={`Chat with ${chat.name}`}
      >
        <div className="side-panel-header">
          <h2 className="side-panel-title">{chat.name}</h2>
          {inPlay && (
            <span className="action-icon" title="Reassign" role="button" tabIndex={0} onClick={() => onReassign(chat)}>👤</span>
          )}
          {canTakeOver && (
            <span className="action-icon" title="Take over" role="button" tabIndex={0} onClick={() => onTakeOver(chat)}>🙋</span>
          )}
          {inPlay && (
            <span className="action-icon action-icon--success" title="Close chat" role="button" tabIndex={0} onClick={() => onCloseChat(chat)}>✓</span>
          )}
          {chat.leadId && (
            <span className="action-icon" title="Open lead" role="button" tabIndex={0} onClick={() => onOpenLead(chat)}>🎯</span>
          )}
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="wa-panel-facts">
          <span>📍 {chat.area}</span>
          {chat.phone && <span>{chat.phone}</span>}
          <span>With {chat.owner}</span>
          <StageBadge stage={chat.stage} />
          {chat.brand === 'pos' && <span className="wa-brand-tag">Peace of Sleep</span>}
          <span className="text-muted">Source: {chat.source}</span>
        </div>

        {chat.escalated && <div className="alert alert-danger wa-panel-alert">No reply in time. Escalated to head office.</div>}
        {!chat.escalated && chat.dueMinutes !== null && chat.dueMinutes <= 0 && (
          <div className="alert alert-danger wa-panel-alert">The reply is overdue by {Math.abs(chat.dueMinutes)} min.</div>
        )}
        {!chat.escalated && chat.dueMinutes !== null && chat.dueMinutes > 0 && (
          <div className="alert alert-warning wa-panel-alert">⏱ {chat.dueMinutes} min left to reply.</div>
        )}
        {chat.aiState === 'ai' && (
          <div className="alert alert-info wa-panel-alert">
            Leo is answering this chat. Reply to take it over.
            {onDrc && <> <button type="button" className="btn-link-primary" onClick={() => onDrc(chat, 'ai_off')}>Switch Leo off for this chat</button></>}
          </div>
        )}
        {(chat.aiState === 'human_required' || chat.aiState === 'human') && (
          <div className={`alert ${chat.aiState === 'human_required' ? 'alert-warning' : 'alert-info'} wa-panel-alert`}>
            {chat.aiState === 'human_required' ? `Leo handed over: ${chat.aiHandover ?? 'a person is needed'}.` : 'A person has this chat; Leo is not answering.'}
            {chat.aiSummary && <> {chat.aiSummary}</>}
            {onDrc && <> <button type="button" className="btn-link-primary" onClick={() => onDrc(chat, 'return_to_ai')}>Return to Leo</button></>}
          </div>
        )}
        {chat.windowClosed && inPlay && (
          <div className="alert alert-info wa-panel-alert">24 hours since the customer last wrote. Only approved messages can be sent.</div>
        )}

        <div className="side-panel-body">
          <div className="wa-thread" ref={thread}>
            {messages.length === 0 && <div className="wa-sys">Loading messages…</div>}
            {messages.map((m) => {
              if (m.sender === 'system') return <div key={m.id} className="wa-sys">{m.body}</div>;
              const label = m.sender === 'auto' ? 'Auto reply' : m.sender === 'note' ? "Team note, customer can't see this" : m.sender === 'assistant' ? 'Leo (virtual assistant)' : m.kind === 'template' ? 'Approved message' : null;
              const shown = m.kind === 'template' ? splitButtons(m.body ?? '') : { text: m.body ?? '', buttons: [] };
              return (
                <div key={m.id} className={`wa-bubble ${m.direction === 'in' ? 'wa-bubble--in' : 'wa-bubble--out'}`}>
                  {label && <span className="wa-bubble-label">{label}</span>}
                  <Media message={m} />
                  <span className="wa-bubble-text">{shown.text}</span>
                  {shown.buttons.map((b) => <span key={b} className="wa-bubble-button">{b}</span>)}
                  {m.signOff && <span className="wa-bubble-label">{m.signOff}</span>}
                  <time className="wa-bubble-time">
                    {m.reaction ? `${m.reaction} ` : ''}{whenLabel(m.createdAt)}{m.direction === 'out' ? ` ${TICKS[m.deliveryStatus] ?? ''}` : ''}
                  </time>
                  {m.direction === 'out' && m.deliveryStatus === 'failed' && (
                    <span className="wa-bubble-failed" role="status">Not delivered{m.error ? `: ${m.error}` : ''}</span>
                  )}
                </div>
              );
            })}
          </div>

          {inPlay && headOffice && !chat.windowClosed && (
            <div className="wa-panel-reply">
              <input
                className="form-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void send(); }}
                placeholder={chat.inbox === 'billing' ? 'Reply (Accounts)' : 'Reply (Customer service)'}
                aria-label="Reply"
                disabled={sending}
              />
              <button type="button" className="btn btn-primary" onClick={() => void send()} disabled={!draft.trim() || sending}>Send</button>
            </div>
          )}
          {inPlay && headOffice && !chat.windowClosed && (
            <label className="checkbox-label wa-panel-sign">
              <input type="checkbox" checked={signWithName} onChange={(e) => setSignWithName(e.target.checked)} />
              Add my first name at the end
            </label>
          )}
          {inPlay && !headOffice && <div className="wa-panel-reply text-muted">Read only. Take over to reply.</div>}
        </div>
      </aside>
    </>
  );
}
